import type { AppDispatch, RootState } from "@store/index";
import { datasetApi } from "./dataset-api";
import {
  reconcileBaseUsage,
  type WidgetBaseMembership,
} from "@utils/dataset-base-usage";
import { serializeBaseConfigJson } from "@models/DatasetBase";
import { getVisualizationBaseSource } from "@models/VisualizationBase";

/** The Dataset-Level Base id a widget's saved config references, or null if it uses none. */
export function extractPreconfiguredBaseId(
  configJson?: string | null,
): string | null {
  if (!configJson) return null;
  try {
    const vb = JSON.parse(configJson)?.visualizationBase;
    if (vb?.enabled && getVisualizationBaseSource(vb) === "preconfigured") {
      return vb.preconfiguredBaseId ?? null;
    }
  } catch {
    // malformed config — treat as no base
  }
  return null;
}

/**
 * Reconciles widget→base membership for one dataset and PATCHes its
 * baseConfigJson only when usage actually changed. Best-effort: any failure is
 * swallowed so it never blocks the widget save/delete it follows.
 *
 * The reconcile is a client-side read-modify-write, so it's inherently racy:
 * two widgets attaching concurrently can both read the array before either
 * write lands, and the second PATCH drops the first's entry. We force a fresh
 * read here (bypassing the possibly-stale RTK cache) to shrink that window as
 * far as the frontend can — but this does NOT eliminate the race. The real fix
 * is a server-side merge of the widgets array at write time.
 */
export async function syncDatasetBaseUsage(
  dispatch: AppDispatch,
  getState: () => RootState,
  datasetId: string | undefined | null,
  updates: WidgetBaseMembership[],
): Promise<void> {
  if (!datasetId || updates.length === 0) return;

  let dataset;
  try {
    dataset = await dispatch(
      datasetApi.endpoints.getDatasetById.initiate(datasetId, {
        forceRefetch: true,
        subscribe: false,
      }),
    ).unwrap();
  } catch {
    return;
  }

  const { bases: nextBases, changed } = reconcileBaseUsage(
    dataset.bases ?? [],
    updates,
  );
  if (!changed) return;

  try {
    await dispatch(
      datasetApi.endpoints.patchDataset.initiate({
        id: datasetId,
        baseConfigJson: serializeBaseConfigJson(nextBases),
      }),
    ).unwrap();
  } catch {
    // best-effort — usage counts are advisory, not an invariant
  }
}

interface RemovableWidget {
  id: string;
  datasetId?: string | null;
  configJson?: string | null;
}

/**
 * Cascade removal (tab/dashboard delete): drops many widgets from their bases,
 * grouped by dataset so each affected dataset gets a single reconciling PATCH.
 */
export async function syncRemovedWidgetsBaseUsage(
  dispatch: AppDispatch,
  getState: () => RootState,
  dashboardId: string,
  widgets: RemovableWidget[],
): Promise<void> {
  const byDataset = new Map<string, WidgetBaseMembership[]>();
  for (const widget of widgets) {
    if (!widget.datasetId) continue;
    const updates = byDataset.get(widget.datasetId) ?? [];
    updates.push({ widgetId: widget.id, dashboardId, baseId: null });
    byDataset.set(widget.datasetId, updates);
  }

  await Promise.all(
    Array.from(byDataset.entries()).map(([datasetId, updates]) =>
      syncDatasetBaseUsage(dispatch, getState, datasetId, updates),
    ),
  );
}
